import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { Camera, Building2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const LoginPage = () => {
  const [userType, setUserType] = useState('company');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = await login(data, userType);
      if (result.success) {
        toast.success('Login successful!');
        reset();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-600 rounded-full">
              <Camera className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Timelapse Manager
          </h2>
          <p className="mt-2 text-gray-600">
            Sign in to your account
          </p>
        </div>

        {/* User Type Selection */}
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => setUserType('company')}
            className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
              userType === 'company'
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Building2 className="h-5 w-5 mr-2" />
            Company
          </button>
          <button
            type="button"
            onClick={() => setUserType('admin')}
            className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
              userType === 'admin'
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Shield className="h-5 w-5 mr-2" />
            Admin
          </button>
        </div>

        {/* Login Form */}
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="form-label">
              {userType === 'admin' ? 'Username' : 'Email'}
            </label>
            <input
              type={userType === 'admin' ? 'text' : 'email'}
              className="form-input"
              placeholder={userType === 'admin' ? 'Enter username' : 'Enter email'}
              {...register(userType === 'admin' ? 'username' : 'email', {
                required: `${userType === 'admin' ? 'Username' : 'Email'} is required`,
                ...(userType === 'company' && {
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Please enter a valid email'
                  }
                })
              })}
            />
            {errors.username && <p className="form-error">{errors.username.message}</p>}
            {errors.email && <p className="form-error">{errors.email.message}</p>}
          </div>

          <div>
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Enter password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              })}
            />
            {errors.password && <p className="form-error">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn btn-primary flex items-center justify-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" className="mr-2" />
                Signing in...
              </>
            ) : (
              `Sign in as ${userType === 'admin' ? 'Admin' : 'Company'}`
            )}
          </button>
        </form>

        {/* Demo Credentials - Only show in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Demo Credentials (Development Only):</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div><strong>Admin:</strong> username: admin, password: admin123</div>
              <div><strong>Company:</strong> Create account or use existing credentials</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
